import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useOpenModal } from 'src/common/components/next-modal';
import { useModel } from 'src/common/create-model';
import { getReadableFileSizeString } from 'src/editor/utils/size-utils';
import { Modals } from 'src/modals';
import { useSpaceCapacity } from 'src/services/capacity';
import { getCurrencySymbols } from 'src/utils/currency-format';
import { UpgradeCouponList, UpgradePaddingUiKit } from './common';
import type { UpgradeSupportParams } from './hook/use-upgrade-support';
import { createUpgradeSupportModel } from './hook/use-upgrade-support';
import { UpgradeComponents } from './upgrade-components';

const UpgradeCapacityContext = createUpgradeSupportModel();

const UpgradeCapacity: FC = () => {
  const { t } = useAppTranslation();
  const {
    couponId,
    selectCoupon,
    onPay,
    close,
    payMethod,
    setPayMethod,
    planId,
    onSelectPlan,
    planList,
    curCalculateQuote,
    loading,
    lastDate,
    couponList,
  } = useModel(UpgradeCapacityContext);
  const spaceCapacity = useSpaceCapacity();

  return (
    <>
      <UpgradeComponents.Header close={close} />
      <div className="px-7">
        <UpgradePaddingUiKit height={54} className="text-h2">
          {t('common.buy_capacity_package')}
        </UpgradePaddingUiKit>
        <UpgradePaddingUiKit height={36} className="text-grey3 text-t3">
          {t('components.select_upgrade_plan.used_capacity', {
            used: getReadableFileSizeString(spaceCapacity.currentCapacity),
          })}
          <span>
            &nbsp;{t('common.total_capacity')}&nbsp;
            {getReadableFileSizeString(spaceCapacity.maxCapacity)}&nbsp;=&nbsp;
          </span>
          <span>
            {getReadableFileSizeString(spaceCapacity.baseCapacity)}
            {t('common.base_capacity')}
          </span>
          <span hidden={!(spaceCapacity.buyCapacity + spaceCapacity.giveCapacity)}>
            &nbsp;+&nbsp;
            {getReadableFileSizeString(spaceCapacity.buyCapacity + spaceCapacity.giveCapacity)}
            {t('components.select_upgrade_plan.supplementary_pack')}
          </span>
          <span hidden={!spaceCapacity.peopleCapacity}>
            &nbsp;+&nbsp;
            {getReadableFileSizeString(spaceCapacity.peopleCapacity)}
            {t('common.seat_capacity')}
          </span>
        </UpgradePaddingUiKit>
      </div>
      {/* 选择套餐 */}
      <UpgradeComponents.SelectPlan
        items={planList}
        loading={loading}
        selectId={planId}
        itemClick={onSelectPlan}
        itemRender={(item) => {
          return (
            <>
              <div className="text-t2-medium mb-2.5">{item.name}</div>
              <div className="text-h3">
                {getCurrencySymbols(item.currentPriceUnit)}
                {item.currentPrice}/{t('common.year')}
              </div>
            </>
          );
        }}
      />
      {/* 购买后的失效日期 */}
      <UpgradeComponents.PlanEndTime endTime={lastDate} />
      {/* 优惠券 */}
      <UpgradeCouponList
        onSelectId={selectCoupon}
        couponList={couponList}
        selectId={couponId}
        className={'px-8 my-2.5'}
      />
      {/* 购买方式 */}
      <UpgradeComponents.PayMethod payMethod={payMethod} switchPayMethod={setPayMethod} />
      {/* 付费 */}
      <UpgradeComponents.PayBar
        price={curCalculateQuote?.amount}
        priceUnit={curCalculateQuote?.amountUnit}
        disablePay={loading}
        onPay={onPay}
      />
    </>
  );
};

export const useOpenUpgradeCapacityModal = () => {
  const openModal = useOpenModal();

  return useCallback(
    (params?: UpgradeSupportParams) =>
      openModal.modal({
        modalId: Modals.UPGRADE_SUPPORT,
        className: 'w-[600px] max-h-[80vh] next-modal-scroll',
        content: () => (
          <UpgradeCapacityContext
            planId={params?.planId}
            isFrom={params?.isFrom}
            upgradeType="capacity"
          >
            <UpgradeCapacity />
          </UpgradeCapacityContext>
        ),
      }),
    [openModal]
  );
};
